import { formatInvoiceSpecs, InvoiceSpecSnapshot } from './invoiceSpecs';

/**
 * Pure invoice printing utility.
 * Renders the printable HTML into an isolated hidden iframe, ensuring NO app chrome,
 * sidebar, modals, dark backdrops, or grey backgrounds are captured during print.
 */

export interface PrintInvoiceData {
  shop: {
    shop_name: string;
    slogan?: string | null;
    address?: string | null;
    phone?: string | null;
    phone_secondary?: string | null;
    phone_tertiary?: string | null;
    email?: string | null;
    website?: string | null;
    invoice_footer?: string | null;
  };
  sale: {
    invoice_number: string;
    sale_date: string;
    payment_status: string;
    subtotal: number;
    discount: number;
    tax: number;
    total: number;
    amount_paid: number;
    balance_due: number;
    notes?: string | null;
    // Snapshot fields (preferred) — fall back to live customer join
    customer_name_snapshot?: string | null;
    customer_phone_snapshot?: string | null;
    customer_email_snapshot?: string | null;
    customer_address_snapshot?: string | null;
    customer?: {
      name: string;
      phone?: string | null;
      address?: string | null;
    } | null;
    items?: Array<InvoiceSpecSnapshot & {
      id: string;
      item_type: 'laptop' | 'accessory' | 'service';
      item_name: string;
      sku_snapshot?: string | null;
      serial_snapshot?: string | null;
      ram_snapshot?: string | null;
      storage_snapshot?: string | null;
      specs_snapshot?: string | null;
      quantity: number;
      unit_price: number;
      line_discount: number;
      line_total: number;
    }>;
    payments?: Array<{
      payment_date?: string;
      amount: number;
      payment_method?: string;
      direction?: 'in' | 'out';
      reference?: string | null;
    }>;
  };
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return 'Rs. 0';
  return `Rs. ${amount.toLocaleString('en-PK')}`;
}

export function generateInvoiceHtml(data: PrintInvoiceData): string {
  const { shop, sale } = data;
  const balanceDue = Math.max(0, sale.balance_due ?? (sale.total - sale.amount_paid));

  // Resolve customer info: prefer immutable snapshots over live join
  const custName = sale.customer_name_snapshot || sale.customer?.name || null;
  const custPhone = sale.customer_phone_snapshot || sale.customer?.phone || null;
  const custAddress = sale.customer_address_snapshot || sale.customer?.address || null;

  const phoneList: string[] = [];
  if (shop.phone) phoneList.push(shop.phone);
  if (shop.phone_secondary) phoneList.push(shop.phone_secondary);
  if (shop.phone_tertiary) phoneList.push(shop.phone_tertiary);

  const itemsRows = (sale.items || [])
    .map((item, index) => {
      const specDetails = formatInvoiceSpecs(item);

      const serialOrSku = item.serial_snapshot
        ? `S/N: ${item.serial_snapshot}`
        : item.sku_snapshot
        ? `SKU: ${item.sku_snapshot}`
        : '';

      return `
      <tr style="border-bottom: 1px solid #e2e8f0; font-size: 11px;">
        <td style="padding: 6px 8px; vertical-align: top; width: 30px; color: #64748b;">${index + 1}</td>
        <td style="padding: 6px 8px; vertical-align: top;">
          <div style="font-weight: 700; color: #0f172a;">${escapeHtml(item.item_name)}</div>
          ${specDetails ? `<div style="font-size: 10px; color: #059669; font-weight: 600; margin-top: 1px;">${escapeHtml(specDetails)}</div>` : ''}
          ${serialOrSku ? `<div style="font-size: 10px; color: #64748b; font-family: monospace; margin-top: 1px;">${escapeHtml(serialOrSku)}</div>` : ''}
        </td>
        <td style="padding: 6px 8px; vertical-align: top; text-align: center; font-weight: 600;">${item.quantity}</td>
        <td style="padding: 6px 8px; vertical-align: top; text-align: right;">${formatMoney(item.unit_price)}</td>
        <td style="padding: 6px 8px; vertical-align: top; text-align: right; color: ${item.line_discount > 0 ? '#dc2626' : '#64748b'};">
          ${item.line_discount > 0 ? `-${formatMoney(item.line_discount)}` : '—'}
        </td>
        <td style="padding: 6px 8px; vertical-align: top; text-align: right; font-weight: 700; color: #0f172a;">
          ${formatMoney(item.line_total)}
        </td>
      </tr>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Invoice #${escapeHtml(sale.invoice_number)} - ${escapeHtml(shop.shop_name)}</title>
  <style>
    @page {
      size: A5 portrait;
      margin: 8mm 10mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #0f172a;
      background: #ffffff;
      font-size: 11px;
      line-height: 1.4;
    }
    .invoice-container {
      width: 100%;
      max-width: 148mm;
      margin: 0 auto;
      background: #ffffff;
    }
    .brand-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #059669;
      padding-bottom: 8px;
      margin-bottom: 10px;
    }
    .brand-title {
      font-size: 18px;
      font-weight: 900;
      color: #0f172a;
      letter-spacing: -0.5px;
      margin: 0;
    }
    .brand-slogan {
      font-size: 9px;
      font-weight: 800;
      color: #dc2626;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-top: 1px;
    }
    .brand-contact {
      font-size: 10px;
      color: #475569;
      margin-top: 3px;
      line-height: 1.35;
    }
    .invoice-badge-box {
      text-align: right;
    }
    .invoice-type {
      font-size: 14px;
      font-weight: 900;
      color: #059669;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    .invoice-num {
      font-size: 12px;
      font-weight: 800;
      color: #0f172a;
      margin-top: 1px;
    }
    .invoice-date {
      font-size: 10px;
      color: #64748b;
      margin-top: 1px;
    }
    .status-pill {
      display: inline-block;
      padding: 2px 8px;
      font-size: 9px;
      font-weight: 800;
      border-radius: 9999px;
      text-transform: uppercase;
      margin-top: 4px;
      background: ${sale.payment_status === 'paid' ? '#dcfce7' : sale.payment_status === 'partial' ? '#fef3c7' : '#fee2e2'};
      color: ${sale.payment_status === 'paid' ? '#166534' : sale.payment_status === 'partial' ? '#92400e' : '#991b1b'};
      border: 1px solid ${sale.payment_status === 'paid' ? '#bbf7d0' : sale.payment_status === 'partial' ? '#fde68a' : '#fecaca'};
    }
    .meta-grid {
      display: flex;
      justify-content: space-between;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 8px 10px;
      margin-bottom: 10px;
      font-size: 10.5px;
    }
    .meta-col {
      flex: 1;
    }
    .meta-label {
      font-size: 9px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #64748b;
      margin-bottom: 2px;
    }
    table.items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 10px;
    }
    table.items-table th {
      background: #f1f5f9;
      color: #334155;
      font-size: 9.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 6px 8px;
      border-bottom: 1px solid #cbd5e1;
    }
    .summary-grid {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
      gap: 12px;
    }
    .notes-box {
      flex: 1;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 6px 8px;
      font-size: 9.5px;
      color: #475569;
    }
    .totals-box {
      width: 190px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 6px 10px;
      font-size: 10.5px;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;
      color: #334155;
    }
    .totals-row.grand-total {
      border-top: 1.5px solid #0f172a;
      margin-top: 4px;
      padding-top: 4px;
      font-weight: 900;
      font-size: 12px;
      color: #0f172a;
    }
    .totals-row.balance-due {
      border-top: 1px dashed #cbd5e1;
      margin-top: 3px;
      padding-top: 3px;
      font-weight: 800;
      color: ${balanceDue > 0 ? '#dc2626' : '#166534'};
    }
    .invoice-footer {
      border-top: 1px solid #e2e8f0;
      padding-top: 8px;
      text-align: center;
      font-size: 9.5px;
      color: #64748b;
      margin-top: 10px;
    }
    .footer-slogan {
      font-weight: 900;
      color: #059669;
      letter-spacing: 1px;
      margin-top: 2px;
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <!-- Header -->
    <div class="brand-header">
      <div style="display: flex; align-items: center; gap: 10px;">
        <img src="/reference/al-kuwait-tech-logo.png" alt="Logo" style="width: 44px; height: 44px; object-fit: contain;" onerror="this.style.display='none'" />
        <div>
          <h1 class="brand-title">${escapeHtml(shop.shop_name || 'AL KUWAIT TECH')}</h1>
          <div class="brand-slogan">${escapeHtml(shop.slogan || 'ALWAYS AHEAD.')}</div>
          <div class="brand-contact">
            ${shop.address ? `<div>${escapeHtml(shop.address)}</div>` : ''}
            ${phoneList.length > 0 ? `<div><strong>Phone:</strong> ${escapeHtml(phoneList.join(' • '))}</div>` : ''}
          </div>
        </div>
      </div>
      <div class="invoice-badge-box">
        <div class="invoice-type">Sales Invoice</div>
        <div class="invoice-num">#INV-${escapeHtml(sale.invoice_number)}</div>
        <div class="invoice-date">${formatDate(sale.sale_date)}</div>
        <div>
          <span class="status-pill">${escapeHtml(sale.payment_status)}</span>
        </div>
      </div>
    </div>

    <!-- Customer Meta -->
    <div class="meta-grid">
      <div class="meta-col">
        <div class="meta-label">Billed To:</div>
        ${
          custName
            ? `<div style="font-weight: 700; color: #0f172a;">${escapeHtml(custName)}</div>
               ${custPhone ? `<div style="color: #475569;">${escapeHtml(custPhone)}</div>` : ''}
               ${custAddress ? `<div style="color: #64748b;">${escapeHtml(custAddress)}</div>` : ''}`
            : `<div style="color: #64748b; font-style: italic;">Walk-in Customer / Cash Sale</div>`
        }
      </div>
      <div class="meta-col" style="text-align: right;">
        <div class="meta-label">Payment Terms:</div>
        <div style="font-weight: 600; color: #0f172a; text-transform: capitalize;">${escapeHtml(sale.payment_status)}</div>
        <div style="color: #64748b; font-size: 9.5px;">Issued on ${formatDate(sale.sale_date)}</div>
      </div>
    </div>

    <!-- Line Items Table -->
    <table class="items-table">
      <thead>
        <tr>
          <th style="text-align: left; width: 25px;">#</th>
          <th style="text-align: left;">Item / Description / Specs</th>
          <th style="text-align: center; width: 40px;">Qty</th>
          <th style="text-align: right; width: 80px;">Price</th>
          <th style="text-align: right; width: 65px;">Disc</th>
          <th style="text-align: right; width: 85px;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <!-- Totals and Notes -->
    <!-- Totals and Notes -->
    <div class="summary-grid">
      <div style="flex: 1; display: flex; flex-direction: column; justify-content: space-between; min-height: 110px;">
        <div class="notes-box">
          <div style="font-weight: 700; margin-bottom: 2px; color: #0f172a;">Invoice Notes / Warranty:</div>
          <div>${sale.notes ? escapeHtml(sale.notes) : 'Standard warranty terms apply as registered in system.'}</div>
        </div>

        <div style="margin-top: 14px; width: 170px;">
          <div style="border-top: 1px solid #334155; padding-top: 4px; font-size: 9.5px; color: #475569; font-weight: 600; letter-spacing: 0.3px; text-align: left;">
            Authorized Signature
          </div>
        </div>
      </div>

      <div class="totals-box">
        <div class="totals-row">
          <span>Subtotal:</span>
          <span style="font-weight: 600;">${formatMoney(sale.subtotal)}</span>
        </div>
        ${
          sale.discount > 0
            ? `<div class="totals-row" style="color: #dc2626;">
                <span>Discount:</span>
                <span>-${formatMoney(sale.discount)}</span>
              </div>`
            : ''
        }
        ${
          sale.tax > 0
            ? `<div class="totals-row">
                <span>Tax:</span>
                <span>+${formatMoney(sale.tax)}</span>
              </div>`
            : ''
        }
        <div class="totals-row grand-total">
          <span>Grand Total:</span>
          <span style="color: #059669;">${formatMoney(sale.total)}</span>
        </div>
        ${
          sale.payments && sale.payments.some((p) => p.direction === 'out')
            ? `<div class="totals-row" style="color: #059669; font-size: 9.5px;">
                <span>Total Received:</span>
                <span>${formatMoney(sale.payments.filter((p) => p.direction !== 'out').reduce((s, p) => s + (p.amount || 0), 0))}</span>
              </div>
              <div class="totals-row" style="color: #dc2626; font-size: 9.5px;">
                <span>Total Refunded:</span>
                <span>-${formatMoney(sale.payments.filter((p) => p.direction === 'out').reduce((s, p) => s + (p.amount || 0), 0))}</span>
              </div>
              <div class="totals-row" style="padding-top: 3px; color: #475569;">
                <span>Net Paid:</span>
                <span style="font-weight: 600;">${formatMoney(sale.amount_paid)}</span>
              </div>`
            : `<div class="totals-row" style="padding-top: 3px; color: #475569;">
                <span>Amount Paid:</span>
                <span style="font-weight: 600;">${formatMoney(sale.amount_paid)}</span>
              </div>`
        }
        <div class="totals-row balance-due">
          <span>Balance Due:</span>
          <span>${formatMoney(balanceDue)}</span>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="invoice-footer">
      <div>${escapeHtml(shop.invoice_footer || 'Thank you for choosing Al Kuwait Tech. We appreciate your business and look forward to serving you again.')}</div>
      <div class="footer-slogan">${escapeHtml(shop.slogan || 'ALWAYS AHEAD.')}</div>
    </div>
  </div>
</body>
</html>`;
}

function escapeHtml(str: string | null | undefined): string {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Initiates isolated printing in a hidden iframe.
 */
export function printInvoiceDocument(data: PrintInvoiceData): void {
  const html = generateInvoiceHtml(data);

  let iframe = document.getElementById('invoice-print-iframe') as HTMLIFrameElement | null;
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = 'invoice-print-iframe';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);
  }

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    // Fallback if iframe fails
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      win.print();
    }
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  // Wait briefly for styles/fonts to paint
  setTimeout(() => {
    try {
      iframe?.contentWindow?.focus();
      iframe?.contentWindow?.print();
    } catch {
      window.print();
    }
  }, 250);
}
